/**
 * Save Payment Method Mutation Hook
 * 
 * React Query hook for saving payment method tokens for future use.
 * 
 * ## Features
 * - Type-safe mutation with legacy API response handling
 * - Automatic query cache invalidation
 * - Success/error callbacks
 * - Non-blocking (doesn't fail booking transaction)
 * 
 * ## Returns the envelope
 * The mutation resolves to `ApiResult<SavePaymentMethodPayload>` and only throws
 * for genuine exceptions. A refused card is `success: false` carrying the API's
 * `message` and `error.code` — callers branch on those rather than on a string.
 * 
 * @module payment/hooks
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '~/lib/query/keys';
import { paymentMethodService } from '~/services/internalApi/payment/api/paymentMethodService';
import type { ApiResult } from '~/lib/apiClient';
import { PaymentSaveError } from '../errors';
import type {
  SavePaymentMethodRequest,
  SavePaymentMethodPayload,
} from '~/services/internalApi/payment/types';

export interface UseSavePaymentMethodOptions {
  /**
   * Tenant handle for precise cache invalidation (optional)
   * 
   * When provided: Invalidates the specific tenant's payment methods cache
   * When omitted: Logs a warning but mutation still succeeds (cache may be stale until next refetch)
   * 
   * @example
   * ```tsx
   * const [tenantHandle] = useTenantHandle();
   * 
   * useSavePaymentMethod({
   *   tenantHandle: tenantHandle || undefined, // May be undefined during app init
   *   // ...
   * });
   * ```
   */
  tenantHandle: string | undefined;
  
  onSuccess?: (response: ApiResult<SavePaymentMethodPayload>) => void;
  onError?: (error: Error) => void;
}

/**
 * Save payment method mutation hook
 * 
 * Call this AFTER successful booking creation to store the payment
 * method for future bookings. Failure does not affect the booking.
 * 
 * **Cache Invalidation:**
 * - If `tenantHandle` is provided: Invalidates tenant-specific payment methods cache
 * - If `tenantHandle` is omitted: Mutation succeeds but cache won't be invalidated (stale until next refetch)
 * 
 * @param options - Configuration options including optional tenantHandle for cache invalidation
 * 
 * @example
 * ```tsx
 * const [tenantHandle] = useTenantHandle();
 * 
 * const savePaymentMethod = useSavePaymentMethod({
 *   tenantHandle: tenantHandle || undefined, // Optional - used for cache invalidation
 *   onSuccess: () => {
 *     butterToast.success('Payment method saved');
 *   },
 *   onError: () => {
 *     // Don't fail - booking already succeeded
 *     console.warn('Could not save payment method');
 *   }
 * });
 * 
 * savePaymentMethod.mutate({
 *   userId: 123,
 *   request: {
 *     provider: 'square',
 *     clubId: 1,
 *     paymentMethodToken: 'cnon:xxx'
 *   }
 * });
 * ```
 */
export function useSavePaymentMethod(options: UseSavePaymentMethodOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    // Never retry: the Square nonce in `paymentMethodToken` is single-use. A
    // retry re-sends a spent token, which Square rejects with SOURCE_USED — so the
    // retry can never succeed, and it doubles the requests behind one click.
    retry: false,

    mutationFn: async ({
      userId,
      request,
    }: {
      userId: number;
      request: SavePaymentMethodRequest;
    }) => {
      const result = await paymentMethodService.savePaymentMethod(
        userId,
        request
      );

      // Throw so TanStack's error path fires and the page's catch can toast —
      // but carry the API's own message, which is the whole point of the move
      // off legacyApiClient. `error.code` rides along for callers that branch
      // (`invalid_card_data` vs `card_declined` vs `card_token_stale`).
      if (!result.success) {
        throw new PaymentSaveError(result);
      }

      return result;
    },

    onSuccess: (response, variables) => {
      // Precise invalidation using tenantHandle (optional)
      if (options.tenantHandle) {
        console.log('[useSavePaymentMethod] Invalidating payment methods cache for tenant:', options.tenantHandle);
        queryClient.invalidateQueries({
          queryKey: queryKeys.tenant.paymentMethods(options.tenantHandle, variables.userId),
        });
      } else {
        // Expected during app initialization or when tenantHandle is unavailable
        // Cache will be stale until next refetch, but mutation still succeeds
        console.warn('[useSavePaymentMethod] tenantHandle not provided - skipping cache invalidation');
      }

      options?.onSuccess?.(response);
    },

    onError: (error: Error) => {
      console.error('[useSavePaymentMethod] Error:', error);
      options?.onError?.(error);
    },
  });
}
